import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSector } from '../../contexts/SectorContext';
import { listApprovals, ApprovalDto } from '../services/MendsVisionService';
import { RegisterRelatoFlow, RelatoStep } from '../components/RegisterRelatoFlow';
import { RegisterHistoryList } from '../components/RegisterHistoryList';
import { MaiSection } from '../components/MaiSection';
import { actionKindLabel, statusText } from '../lib/approvalLabels';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import {
  openRegisterConversation,
  inferSourceSurface,
  sourceSurfaceLabel,
  RegisterSourceSurface,
} from '../openRegister';
import '../styles/mendsAi.css';

type RegTab = 'novo' | 'resultado' | 'esclarecer' | 'historico';

function resolveTab(raw: string | null): RegTab {
  if (raw === 'resultado' || raw === 'result') return 'resultado';
  if (raw === 'esclarecer' || raw === 'clarify') return 'esclarecer';
  if (raw === 'historico' || raw === 'history') return 'historico';
  return 'novo';
}

const RegisterPage: React.FC = () => {
  const { selectedSectorId } = useSector();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = resolveTab(params.get('tab'));
  const [approvals, setApprovals] = useState<ApprovalDto[]>([]);
  /** Muda a cada relato processado: a lista do servidor recarrega. */
  const [historyKey, setHistoryKey] = useState(0);
  const [historyCount, setHistoryCount] = useState(0);
  const [logClarifyCount, setLogClarifyCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastStep, setLastStep] = useState<RelatoStep>('capture');
  /** Contato que o relato identificou (sem vir da tela de origem). */
  const [identified, setIdentified] = useState<{ id: number; name: string } | null>(null);
  const contactId = params.get('contactId') ? Number(params.get('contactId')) : null;
  const contactName = params.get('contactName') || undefined;
  const conversationId = params.get('conversationId')
    ? Number(params.get('conversationId'))
    : null;
  const opportunityId = params.get('opportunityId') || undefined;
  const sourceSurface = (params.get('sourceSurface') as RegisterSourceSurface | null)
    || inferSourceSurface(
      params.get('screen') || undefined,
      typeof window !== 'undefined' ? window.location.pathname : '/registrar',
    );

  const setTab = (next: RegTab) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'novo') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams);
  };

  const refreshRegisterLog = useCallback(() => setHistoryKey((k) => k + 1), []);

  const loadApprovals = useCallback(async () => {
    if (!selectedSectorId) return;
    setLoading(true);
    setError(null);
    try {
      setApprovals(await listApprovals(Number(selectedSectorId)));
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Falha ao carregar fila de aprovações'));
    } finally {
      setLoading(false);
    }
  }, [selectedSectorId]);

  useEffect(() => {
    refreshRegisterLog();
  }, [refreshRegisterLog, tab, lastStep]);

  useEffect(() => {
    if (tab === 'esclarecer' || tab === 'historico' || tab === 'resultado') {
      void loadApprovals();
    }
  }, [tab, loadApprovals]);

  const approvalsClarify = useMemo(
    () => approvals.filter((a) => a.status === 'needs_info'),
    [approvals],
  );

  const clarifyCount = logClarifyCount + approvalsClarify.length;

  // Só o que veio da tela de origem conta como vínculo — um relato antigo não é "contexto reconhecido".
  const contextContact = contactName || null;

  const onFlowConfirmed = () => {
    refreshRegisterLog();
    void loadApprovals();
  };

  return (
    <div className="mai-page mai-registrar">
      <header className="mai-page-header">
        <div className="mai-title-row">
          <div>
            <h1 className="mai-title">Registrar</h1>
            <p className="mai-subtitle">
              Conte o que aconteceu — texto ou voz. A MENDS executa o autorizado.
              Ctrl+K continua sendo a Barra da IA (consulta/comando).
            </p>
          </div>
          <div className="mai-btn-row">
            <button
              type="button"
              className="mai-btn mai-btn-soft"
              onClick={() => {
                setTab('novo');
                window.setTimeout(() => {
                  document.querySelector<HTMLButtonElement>('.mai-reg-mic')?.focus();
                }, 80);
              }}
            >
              Focar voz no registro
            </button>
            <button
              type="button"
              className="mai-btn mai-btn-ghost"
              onClick={() => openRegisterConversation({
                contactId: contactId || undefined,
                contactName,
                conversationId: conversationId || undefined,
                opportunityId,
                sourceSurface,
                screen: 'register',
                pathname: '/registrar',
              })}
            >
              Widget rápido
            </button>
          </div>
        </div>
      </header>

      {!selectedSectorId && (
        <p className="mai-empty mai-reg-empty-sector">
          Selecione um setor/conexão no menu para registrar relatos e ver o histórico deste workspace.
        </p>
      )}

      <div className="mai-hub-tabs mai-reg-tabs" role="tablist" aria-label="Registrar">
        <button type="button" role="tab" aria-selected={tab === 'novo'} className={tab === 'novo' ? 'mai-tab is-active' : 'mai-tab'} onClick={() => setTab('novo')}>
          Novo registro
        </button>
        <button type="button" role="tab" aria-selected={tab === 'resultado'} className={tab === 'resultado' ? 'mai-tab is-active' : 'mai-tab'} onClick={() => setTab('resultado')}>
          Registro e resultado
        </button>
        <button type="button" role="tab" aria-selected={tab === 'esclarecer'} className={tab === 'esclarecer' ? 'mai-tab is-active' : 'mai-tab'} onClick={() => setTab('esclarecer')}>
          Precisam de esclarecimento
          {clarifyCount > 0 && (
            <span className="mai-pill mai-pill-wait" style={{ marginLeft: 6 }}>{clarifyCount}</span>
          )}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'historico'} className={tab === 'historico' ? 'mai-tab is-active' : 'mai-tab'} onClick={() => setTab('historico')}>
          Histórico
          {historyCount > 0 && (
            <span className="mai-pill" style={{ marginLeft: 6 }}>{Math.min(historyCount, 99)}</span>
          )}
        </button>
      </div>

      {error && <p className="mai-form-error">{error}</p>}

      <div className="mai-reg-page-grid">
        <div className="mai-reg-page-main">
          {(tab === 'novo' || tab === 'resultado') && (
            <>
              {tab === 'resultado' && lastStep === 'capture' && (
                <p className="mai-empty">
                  Interprete um relato em “Novo registro” para ver o resultado aqui.
                </p>
              )}
              {tab === 'resultado' && lastStep === 'clarify' && (
                <p className="mai-card-detail" style={{ marginBottom: 10 }}>
                  Esclarecimento de identidade em andamento — escolha o contato abaixo antes de continuar.
                </p>
              )}
              <RegisterRelatoFlow
                defaultContactId={contactId}
                defaultContactName={contactName}
                sourceSurface={sourceSurface}
                conversationId={conversationId}
                opportunityId={opportunityId}
                initialText={params.get('q') || undefined}
                autoRunInitial={params.get('run') === '1'}
                screenContext={{
                  screen: 'register',
                  pathname: '/registrar',
                  contactId: contactId || undefined,
                  contactName,
                  conversationId: conversationId || undefined,
                  subjectKind: contactId ? 'qualified_contact' : undefined,
                }}
                onStepChange={(step, meta) => {
                  setLastStep(step);
                  const p = meta?.plan;
                  setIdentified(step === 'result' && p?.contactId
                    ? { id: p.contactId, name: p.contactName || p.contactNameGuess || `Contato #${p.contactId}` }
                    : null);
                  refreshRegisterLog();
                  // clarify/result ficam em novo|resultado — trocar para "esclarecer"
                  // desmontava o RegisterRelatoFlow e perdia candidatos REG-09 / CONTACT_REQUIRED.
                  if (step === 'result' || step === 'clarify') {
                    const nextParams = new URLSearchParams(params);
                    nextParams.set('tab', 'resultado');
                    setParams(nextParams);
                  }
                }}
                onConfirmed={onFlowConfirmed}
              />
            </>
          )}

          {tab === 'esclarecer' && (
            <>
              <MaiSection label="Relatos aguardando identidade" count={logClarifyCount}>
                {selectedSectorId ? (
                  <RegisterHistoryList
                    sectorId={Number(selectedSectorId)}
                    status="awaiting_clarification"
                    refreshKey={historyKey}
                    onCount={setLogClarifyCount}
                    emptyText="Nenhum relato aguardando esclarecimento de contato."
                  />
                ) : null}
                {logClarifyCount > 0 && (
                  <div className="mai-btn-row">
                    <button type="button" className="mai-btn" onClick={() => setTab('novo')}>
                      Retomar no novo registro
                    </button>
                  </div>
                )}
              </MaiSection>

              <MaiSection
                label="Perguntas da IA na fila de aprovações"
                count={approvalsClarify.length}
                loading={loading}
                showEmpty={!loading && approvalsClarify.length === 0}
                empty="Nenhuma pergunta da IA na fila de aprovações agora."
              >
                {approvalsClarify.map((a) => (
                  <div key={a.id} className="mai-card mai-reg-log-card">
                    <p className="mai-card-title">{a.title}</p>
                    <p className="mai-card-detail">
                      {a.clarificationQuestion || a.body || 'Precisa de informação adicional.'}
                    </p>
                    <div className="mai-btn-row">
                      <button
                        type="button"
                        className="mai-btn"
                        onClick={() => navigate(`/central-inteligencia?tab=aprovacoes&id=${a.id}`)}
                      >
                        Responder na fila
                      </button>
                      <button
                        type="button"
                        className="mai-btn mai-btn-soft"
                        onClick={() => setTab('novo')}
                      >
                        Novo relato
                      </button>
                    </div>
                  </div>
                ))}
              </MaiSection>

              <p className="mai-card-detail mai-reg-clarify-resume">
                O texto do relato fica salvo como rascunho. Em “Novo registro”, interprete de novo e escolha o
                contato quando eu perguntar — nada é gravado no cliente antes disso.
              </p>
            </>
          )}

          {tab === 'historico' && (
            <MaiSection label="Histórico do Registrar" count={historyCount}>
              <p className="mai-card-detail mai-reg-log-note">
                Relatos gravados no servidor, com autor, origem e o estado atual de cada item — a mesma lista para
                qualquer pessoa da equipe neste setor, em qualquer dispositivo.
              </p>
              {selectedSectorId ? (
                <RegisterHistoryList
                  sectorId={Number(selectedSectorId)}
                  refreshKey={historyKey}
                  onCount={setHistoryCount}
                  emptyText="Ainda não há relatos neste setor. Registre um em “Novo registro”."
                />
              ) : (
                <p className="mai-empty">Selecione um setor para ver o histórico do Registrar.</p>
              )}

              {approvals.length > 0 && (
                <details className="mai-reg-approvals-fold">
                  <summary>Fila de aprovações do setor ({approvals.length})</summary>
                  <p className="mai-card-detail" style={{ margin: '8px 0 12px' }}>
                    Fila compartilhada com a equipe — mostra o que está em aprovação, não só os seus relatos.
                  </p>
                  {approvals.slice(0, 15).map((a) => (
                    <div key={a.id} className="mai-card mai-reg-log-card">
                      <div className="mai-card-row">
                        <div>
                          <p className="mai-card-title">{a.title}</p>
                          <p className="mai-card-detail">
                            {a.contactName ? `${a.contactName} · ` : ''}
                            {statusText(a.status).label}
                            {a.actionKind ? ` · ${actionKindLabel(a.actionKind)}` : ''}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="mai-btn mai-btn-ghost"
                          onClick={() => navigate(`/central-inteligencia?tab=aprovacoes&id=${a.id}`)}
                        >
                          Abrir
                        </button>
                      </div>
                    </div>
                  ))}
                </details>
              )}
            </MaiSection>
          )}
        </div>

        <aside className="mai-reg-page-aside" aria-label="Contexto reconhecido">
          <div className="mai-card">
            <h3 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>Contexto reconhecido</h3>
            {!contextContact && !contactId && identified ? (
              <>
                <p className="mai-card-title">{identified.name}</p>
                <p className="mai-card-detail">Contato #{identified.id} · identificado pelo relato</p>
                <span className="mai-pill mai-pill-ai">Identificado</span>
                <div className="mai-btn-row" style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="mai-btn mai-btn-ghost"
                    onClick={() => navigate(`/chat?contactId=${identified.id}`)}
                  >
                    Ver conversa
                  </button>
                </div>
              </>
            ) : contextContact || contactId ? (
              <>
                <p className="mai-card-title">{contactName || contextContact}</p>
                {contactId && <p className="mai-card-detail">Contato #{contactId}</p>}
                <p className="mai-card-detail">Origem: {sourceSurfaceLabel(sourceSurface)}</p>
                {conversationId ? (
                  <p className="mai-card-detail">Conversa #{conversationId}</p>
                ) : null}
                {opportunityId ? (
                  <p className="mai-card-detail">Oportunidade {opportunityId}</p>
                ) : null}
                <span className="mai-pill mai-pill-ai">Identificado / vinculado</span>
                <div className="mai-btn-row" style={{ marginTop: 12 }}>
                  {contactId && (
                    <>
                      <button
                        type="button"
                        className="mai-btn mai-btn-soft"
                        onClick={() => navigate(`/contacts`)}
                      >
                        Abrir clientes
                      </button>
                      <button
                        type="button"
                        className="mai-btn mai-btn-ghost"
                        onClick={() => navigate(`/chat?contactId=${contactId}`)}
                      >
                        Ver conversa
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <p className="mai-card-detail">
                Sem cliente pré-selecionado. A IA tenta identificar pelo relato; se houver ambiguidade,
                pede esclarecimento antes de executar.
              </p>
            )}
          </div>
          <div className="mai-card" style={{ marginTop: 12 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>Como funciona</h3>
            <ul className="mai-reg-howto">
              <li>Segure o microfone, fale e solte — como no WhatsApp. Ou digite.</li>
              <li>Fatos, estágio e retornos com data são executados na hora, em seu nome.</li>
              <li>Desconto, envio imediato e decisões sensíveis vão para aprovação.</li>
              <li>Cliente por nome ou telefone; se houver dois “João”, pergunto qual.</li>
              <li>Cada item mostra o status real: registrado, programado ou aguardando.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default RegisterPage;
